import React from 'react';
import type { ThreadMessage } from 'lib.store/src/type-partner/ai-agent/models';

/**
 * Agent widgets: functional frontend components carried by agent-thread
 * messages of type "widget". The message `meta` is the descriptor —
 * `meta.kind` names the widget, the rest of `meta` are its props — and the
 * message text stays the readable fallback for surfaces (and the agent)
 * without a renderer for that kind.
 *
 * Making ANY component widget-compatible is one call at app startup:
 *
 *   registerAgentWidget('my_kind', MyWidget);
 *
 * where MyWidget accepts AgentWidgetProps. Every agent surface that renders
 * messages through AiAssistantMessage picks it up automatically, and the same
 * component stays usable anywhere else as a plain React component.
 */
export interface AgentWidgetProps {
  /** the widget descriptor from the message meta (kind + props) */
  meta: Record<string, unknown>;
  /** the carrying message — fallback text, author, timestamps */
  message: ThreadMessage;
}

export type AgentWidgetComponent = React.FC<AgentWidgetProps>;

const registry = new Map<string, AgentWidgetComponent>();

/**
 * Registers a component as the renderer of a widget kind. Last registration
 * wins — apps register their widget set once at startup.
 */
export const registerAgentWidget = (kind: string, component: AgentWidgetComponent): void => {
  registry.set(kind, component);
};

/** The renderer of a message's widget kind, when one is registered */
export const resolveAgentWidget = (message: ThreadMessage): AgentWidgetComponent | null => {
  if (message.type?.name !== 'widget') return null;
  const kind = message.meta?.kind;
  return typeof kind === 'string' ? (registry.get(kind) ?? null) : null;
};

/**
 * Renders the message's widget; `fallback` (usually the plain message bubble)
 * shows when no renderer is registered for its kind.
 */
export const AgentWidget: React.FC<{ message: ThreadMessage; fallback?: React.ReactNode }> = ({
  message,
  fallback = null,
}) => {
  const Widget = resolveAgentWidget(message);
  if (!Widget || !message.meta) return <>{fallback}</>;
  return <Widget meta={message.meta} message={message} />;
};
